var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { darken } from 'polished';
import { darken as darkenT } from '@theme-ui/color';
import 'typeface-source-sans-pro';
export var colors = {
    background: '#fff',
    text: '#222',
    dark: '#0b314d',
    highlight: '#d8779f',
    primary: '#40bfaa',
    secondary: '#205072',
    success: '#28a745',
    danger: '#b71a3e',
    warning: '#f2bd0d',
    info: '#007bff',
    muted: '#647687',
    grey: '#ced4da',
    light: '#f2f5fd',
};
export var boxShadow = '1px 1px 4px 0px rgba(0,0,0,0.1)';
export var borderRadius = 3;
export var focusStyle = {
    outlineStyle: 'solid',
    outlineWidth: 3,
    outlineColor: colors.info,
};
var fonts = {
    body: "'Source Sans Pro', 'Roboto', 'Oswald', 'Helvetica Neue', 'Arial', sans-serif",
    heading: "'Source Sans Pro', 'Roboto', 'Oswald', 'Helvetica Neue', 'Arial', sans-serif",
    monospace: 'Menlo, monospace',
};
export var fieldBaseStyle = {
    fontFamily: fonts.body,
    fontSize: 2,
    borderStyle: 'solid',
    borderColor: 'grey',
    borderWidth: 1,
    bg: 'background',
    p: 2,
    lineHeight: 1.5,
    '&:focus': focusStyle,
    borderRadius: borderRadius,
    width: '100%',
    '&:disabled': {
        bg: '#f5f5f5',
    },
};
export var defaultTheme = {
    useCustomProperties: false,
    colors: colors,
    borderRadius: borderRadius,
    boxShadow: boxShadow,
    focusStyle: focusStyle,
    breakpoints: ['540px', '770px', '960px'],
    space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
    fonts: fonts,
    fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 72],
    fontWeights: {
        body: 400,
        heading: 600,
        bold: 600,
    },
    lineHeights: {
        body: 1.5,
        heading: 1.125,
    },
    zIndices: {
        dropdown: 900,
        modal: 950,
        popover: 1000,
    },
    styles: {
        root: {
            fontFamily: 'body',
            lineHeight: 'body',
            fontWeight: 'body',
        },
        h1: {
            color: 'primary',
            fontFamily: 'heading',
            lineHeight: 'heading',
            fontWeight: 'heading',
            fontSize: 5,
        },
        h2: {
            color: 'primary',
            fontFamily: 'heading',
            lineHeight: 'heading',
            fontWeight: 'heading',
            fontSize: 4,
        },
        h3: {
            color: 'primary',
            fontFamily: 'heading',
            lineHeight: 'heading',
            fontWeight: 'heading',
            fontSize: 3,
        },
        h4: {
            color: 'primary',
            fontFamily: 'heading',
            lineHeight: 'heading',
            fontWeight: 'heading',
            fontSize: 2,
        },
        h5: {
            color: 'primary',
            fontFamily: 'heading',
            lineHeight: 'heading',
            fontWeight: 'heading',
            fontSize: 1,
        },
        h6: {
            color: 'primary',
            fontFamily: 'heading',
            lineHeight: 'heading',
            fontWeight: 'heading',
            fontSize: 0,
        },
        p: {
            color: 'text',
            fontFamily: 'body',
            fontWeight: 'body',
            lineHeight: 'body',
        },
        a: {
            color: 'primary',
        },
        pre: {
            fontFamily: 'monospace',
            overflowX: 'auto',
            code: {
                color: 'inherit',
            },
        },
        code: {
            fontFamily: 'monospace',
            fontSize: 'inherit',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            borderSpacing: 0,
            borderBottomStyle: 'solid',
            borderWidth: 3,
            borderColor: 'secondary',
            th: {
                textAlign: 'left',
                color: 'white',
                bg: 'secondary',
            },
            'td,th': {
                borderStyle: 'solid',
                borderColor: 'grey',
                borderWidth: 1,
                px: 2,
                py: 1,
            },
        },
        th: {
            textAlign: 'left',
            borderBottomStyle: 'solid',
        },
        td: {
            textAlign: 'left',
            borderBottomStyle: 'solid',
        },
        img: {
            maxWidth: '100%',
        },
    },
    buttons: {
        base: {
            borderRadius: 3,
            borderStyle: 'solid',
            borderWidth: 1,
            borderColor: 'transparent',
            fontFamily: fonts.body,
            lineHeight: 1.5,
            fontWeight: 'bold',
            fontSize: 2,
            py: 2,
            px: 3,
            '&:disabled': {
                color: '#bbb !important',
                bg: '#fff !important',
                boxShadow: 'none !important',
                cursor: 'not-allowed',
                borderWidth: 1,
                borderColor: '#eee',
                borderStyle: 'solid',
            },
            '&:focus': focusStyle,
        },
        variant: {
            primary: {
                bg: 'primary',
                color: 'white',
                '&:hover, &.active': {
                    bg: darkenT('primary', 0.05),
                    boxShadow: boxShadow,
                },
            },
            filledSecondary: {
                bg: 'secondary',
                color: 'white',
                '&:hover, &.active': {
                    bg: darkenT('secondary', 0.05),
                    boxShadow: boxShadow,
                },
            },
            highlight: {
                bg: 'highlight',
                color: 'white',
                '&:hover, &.active': {
                    bg: darkenT('highlight', 0.05),
                    boxShadow: boxShadow,
                },
            },
            grey: {
                bg: 'transparent',
                borderColor: 'grey',
                color: 'grey',
                '&:hover, &.active': {
                    color: 'secondary',
                    boxShadow: boxShadow,
                },
            },
            filledLightGrey: {
                bg: '#f5f5f5',
                color: 'dark',
                '&:hover, &.active': {
                    bg: 'secondary',
                    color: 'white',
                    boxShadow: boxShadow,
                },
            },
            muted: {
                bg: 'transparent',
                borderColor: 'muted',
                color: 'muted',
                '&:hover, &.active': {
                    bg: 'muted',
                    color: 'white',
                    boxShadow: boxShadow,
                },
            },
            secondary: {
                bg: 'white',
                borderColor: 'secondary',
                color: 'secondary',
                '&:hover, &.active': {
                    bg: 'secondary',
                    color: 'white',
                    boxShadow: boxShadow,
                },
            },
            success: {
                bg: 'white',
                borderColor: 'success',
                color: 'success',
                '&:hover, &.active': {
                    bg: 'success',
                    color: 'white',
                    boxShadow: boxShadow,
                },
            },
            warning: {
                bg: 'warning',
                color: 'text',
                '&:hover, &.active': {
                    bg: darkenT('warning', 0.1),
                    boxShadow: boxShadow,
                },
            },
            danger: {
                bg: 'danger',
                color: 'white',
                '&:hover, &.active': {
                    bg: darkenT('danger', 0.05),
                    boxShadow: boxShadow,
                },
            },
            dangerOutline: {
                bg: 'white',
                borderColor: 'danger',
                color: 'danger',
                '&:hover, &.active': {
                    bg: 'danger',
                    color: 'white',
                    boxShadow: boxShadow,
                },
            },
            info: {
                bg: 'info',
                color: 'white',
                '&:hover, &.active': {
                    bg: darken(0.05, colors.info),
                    boxShadow: boxShadow,
                },
            },
            nav: {
                bg: 'transparent',
                color: 'white',
                '&:hover': {
                    bg: darken(0.1, colors.dark),
                },
            },
            link: {
                background: 'none',
                color: 'primary',
                textDecoration: 'none',
                m: 0,
                p: 0,
                display: 'inline',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                '&:hover': {
                    color: darken(0.1, colors.primary),
                    textDecoration: 'underline',
                },
            },
        },
        size: {
            sm: {
                py: 1,
                px: 2,
                fontSize: 1,
            },
            md: {},
            lg: {
                fontSize: 3,
                px: 3,
            },
        },
    },
    badges: {
        base: {
            borderRadius: borderRadius,
            fontWeight: 'bold',
        },
        variant: {
            primary: {
                bg: 'primary',
                color: 'white',
            },
            highlight: {
                bg: 'highlight',
                color: 'white',
            },
            secondary: {
                bg: 'secondary',
                color: 'white',
            },
            success: {
                bg: 'success',
                color: 'white',
            },
            info: {
                bg: 'info',
                color: 'white',
            },
            warning: {
                bg: 'warning',
                color: 'text',
            },
            danger: {
                bg: 'danger',
                color: 'white',
            },
            grey: {
                bg: 'grey',
                color: 'text',
            },
        },
        size: {
            sm: {
                fontSize: 0,
            },
            md: {},
        },
        badgeStyle: {
            normal: {},
            pill: {
                borderRadius: '0.75rem',
                px: '0.75rem',
            },
        },
    },
    spinners: {
        size: {
            md: { fontSize: 2 },
            lg: { fontSize: 5 },
            xl: { fontSize: 8 },
        },
    },
    dropdownMenu: {
        boxShadow: boxShadow,
        borderRadius: borderRadius,
    },
    dropdownMenuItem: {
        fontFamily: 'body',
        lineHeight: 'body',
        fontWeight: 'body',
        '&:hover': {
            bg: 'light',
        },
        '&:focus': focusStyle,
    },
    iconButtons: {
        base: {
            bg: 'transparent',
            px: 1,
            py: 1,
            color: 'muted',
            borderColor: 'transparent',
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: borderRadius,
            '&:focus': focusStyle,
            '&:disabled, &:hover:disabled': {
                color: 'grey',
                cursor: 'default',
            },
        },
        variant: {
            primary: {
                '&:hover, &.active': {
                    color: 'primary',
                    borderColor: 'primary',
                },
                '&:hover.active': {
                    color: darken(0.2, colors.primary),
                    borderColor: darken(0.2, colors.primary),
                },
            },
            secondary: {
                '&:hover, &.active': {
                    color: 'secondary',
                    borderColor: 'secondary',
                },
                '&:hover.active': {
                    color: darken(0.2, colors.secondary),
                    borderColor: darken(0.2, colors.secondary),
                },
            },
            highlight: {
                '&:hover, &.active': {
                    color: 'highlight',
                },
                '&:hover.active': {
                    color: darken(0.2, colors.highlight),
                },
            },
            warning: {
                '&:hover, &.active': {
                    color: 'warning',
                },
                '&:hover.active': {
                    color: darken(0.2, colors.warning),
                },
            },
            danger: {
                '&:hover, &.active': {
                    color: 'danger',
                },
                '&:hover.active': {
                    color: darken(0.1, colors.danger),
                },
            },
            info: {
                '&:hover, &.active': {
                    color: 'info',
                },
                '&:hover.active': {
                    color: darken(0.1, colors.info),
                },
            },
        },
        size: {
            sm: {
                fontSize: 1,
            },
            md: {
                fontSize: 2,
            },
            lg: {
                fontSize: 4,
            },
        },
    },
    textFields: {
        base: __assign({}, fieldBaseStyle),
        variant: {
            default: {},
            success: {
                borderColor: 'success',
            },
            error: {
                borderColor: 'danger',
            },
        },
    },
    selects: {
        base: __assign({}, fieldBaseStyle),
        variant: {
            default: {},
            success: {
                borderColor: 'success',
            },
            error: {
                borderColor: 'danger',
            },
        },
    },
    clickable: {
        '&:focus': focusStyle,
    },
    labels: {
        base: {
            fontSize: 3,
            color: 'secondary',
        },
        variant: {
            error: {
                color: 'danger',
            },
        },
    },
    toasts: {
        base: {
            boxShadow: boxShadow,
        },
        variant: {
            primary: {
                borderLeftColor: 'primary',
                '& .title, & .icon': { color: 'primary' },
            },
            secondary: {
                borderLeftColor: 'secondary',
                '& .title, & .icon': { color: 'secondary' },
            },
            success: {
                borderLeftColor: 'success',
                '& .title, & .icon': { color: 'success' },
            },
            warning: {
                borderLeftColor: 'warning',
                '& .title, & .icon': { color: 'warning' },
            },
            danger: {
                borderLeftColor: 'danger',
                '& .title, & .icon': { color: 'danger' },
            },
            info: {
                borderLeftColor: 'info',
                '& .title, & .icon': { color: 'info' },
            },
        },
    },
    toggleSwitches: {
        base: {
            '& focus + div': focusStyle,
        },
        variant: {
            primary: {
                '& .slider.toggled': { bg: 'primary' },
                '&:hover .slider.toggled': { bg: darken(0.1, colors.primary) },
            },
            secondary: {
                '& .slider.toggled': { bg: 'secondary' },
                '&:hover .slider.toggled': { bg: darken(0.1, colors.secondary) },
            },
            warning: {
                '& .slider.toggled': { bg: 'warning' },
                '&:hover .slider.toggled': { bg: darken(0.1, colors.warning) },
            },
            danger: {
                '& .slider.toggled': { bg: 'danger' },
                '&:hover .slider.toggled': { bg: darken(0.1, colors.danger) },
            },
            success: {
                '& .slider.toggled': { bg: 'success' },
                '&:hover .slider.toggled': { bg: darken(0.1, colors.success) },
            },
            info: {
                '& .slider.toggled': { bg: 'info' },
                '&:hover .slider.toggled': { bg: darken(0.1, colors.info) },
            },
        },
    },
};
//# sourceMappingURL=theme.js.map